// DAS-3094: Agentic Overview (DEV only) — every AI agent session with its cost,
// daily and weekly sums per kind of agent, totals and the monthly cap.
// DAS-3100: second tab "Workflows" — every agentic workflow, its trigger, last run, 30-day cost.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Table, Tag, Button, Alert, Spin, Space, Statistic, Row, Col, Select, Tabs } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import api from '../functions/api';

const usd = (v) => `$${Number(v || 0).toFixed(2)}`;
const mins = (s) => (s == null ? '—' : s < 60 ? `${s}s` : `${Math.round(s / 60)} min`);

/** rows {day|week_start, kind, cost_usd, sessions} -> one row per period, one column per kind */
function pivot(rows, periodKey) {
  const kinds = Array.from(new Set(rows.map((r) => r.kind))).sort();
  const byPeriod = new Map();
  rows.forEach((r) => {
    const p = r[periodKey];
    if (!byPeriod.has(p)) byPeriod.set(p, { period: p, total: 0, sessions: 0 });
    const row = byPeriod.get(p);
    row[r.kind] = (row[r.kind] || 0) + Number(r.cost_usd || 0);
    row.total += Number(r.cost_usd || 0);
    row.sessions += Number(r.sessions || 0);
  });
  const data = Array.from(byPeriod.values()).sort((a, b) => (a.period < b.period ? 1 : -1));
  const columns = [
    { title: periodKey === 'day' ? 'Day' : 'Week of', dataIndex: 'period', key: 'period', width: 110 },
    ...kinds.map((k) => ({ title: k, dataIndex: k, key: k, align: 'right', render: (v) => (v ? usd(v) : '') })),
    { title: 'Sessions', dataIndex: 'sessions', key: 'sessions', align: 'right', width: 90 },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', width: 90, render: (v) => <b>{usd(v)}</b> },
  ];
  return { data, columns };
}

const LEVEL_COLOR = { 0: 'default', 1: 'blue', 2: 'orange', 3: 'red' };
const LEVEL_NAME = { 0: 'no model', 1: 'model call', 2: 'bounded agentic', 3: 'autonomous agentic' };
const TRIGGER_LABEL = { cron: 'scheduled', runner: 'Mac runner', button: 'button / user', 'called-by': 'called by another job', unknown: 'unknown' };

const AgenticOverviewModal = ({ isVisible, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(14);
  const [levelFilter, setLevelFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/agentic_overview?days=${days}&weeks=8`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (isVisible) load();
  }, [isVisible, load]);

  const daily = useMemo(() => pivot(data?.daily || [], 'day'), [data]);
  const weekly = useMemo(() => pivot(data?.weekly || [], 'week_start'), [data]);
  const today = new Date().toISOString().slice(0, 10);
  const todayCost = (data?.daily || []).filter((r) => r.day === today).reduce((a, r) => a + Number(r.cost_usd || 0), 0);
  const capPct = data?.cap_usd ? Math.round((100 * (data.totals?.month_cost_usd || 0)) / data.cap_usd) : 0;

  const sessionColumns = [
    { title: 'Started', dataIndex: 'started_at', key: 'started_at', width: 150 },
    { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 150, render: (k) => <Tag>{k}</Tag> },
    { title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true },
    { title: 'Model', dataIndex: 'model', key: 'model', width: 150, ellipsis: true },
    { title: 'Host', dataIndex: 'host', key: 'host', width: 60 },
    { title: 'Duration', dataIndex: 'duration_s', key: 'duration_s', width: 85, align: 'right', render: mins },
    { title: 'Cost', dataIndex: 'cost_usd', key: 'cost_usd', width: 80, align: 'right', render: usd },
    {
      title: 'Outcome', dataIndex: 'success', key: 'success', width: 90,
      render: (s, r) => (s == null ? <Tag>running</Tag> : s ? <Tag color="green">ok</Tag> : <Tag color="red" title={r.summary}>failed</Tag>),
    },
  ];

  const wf = data?.workflows;
  const workflowRows = (wf?.workflows || []).filter((r) => levelFilter === 'all' || String(r.level) === String(levelFilter));
  const workflowColumns = [
    {
      title: 'Level', dataIndex: 'level', key: 'level', width: 130,
      render: (lv, r) => <span><Tag color={LEVEL_COLOR[lv]}>{lv} · {LEVEL_NAME[lv]}</Tag>{r.acts_unattended ? <><br /><Tag color="volcano">acts on its own</Tag></> : null}</span>,
    },
    { title: 'Workflow', dataIndex: 'name', key: 'name', render: (n, r) => <span><b>{n}</b><br /><span style={{ color: '#888' }}>{r.kind} · {r.script}</span></span> },
    { title: 'Trigger', key: 'trigger', width: 230, render: (_, r) => <span><Tag>{TRIGGER_LABEL[r.trigger_kind] || r.trigger_kind}</Tag><br />{r.trigger_detail}</span> },
    { title: 'Host', dataIndex: 'host', key: 'host', width: 60 },
    { title: 'Shared launcher', dataIndex: 'shared_launcher', key: 'shared', width: 110, render: (v) => (v ? <Tag color="green">yes</Tag> : <Tag>no model</Tag>) },
    {
      title: 'Last run', key: 'last', width: 220,
      render: (_, r) => (r.last_started_at ? (
        <span>{String(r.last_started_at).slice(0, 16)} · {mins(r.last_duration_s)} · {usd(r.last_cost_usd)}<br />
          {r.last_success == null ? <Tag>running</Tag> : r.last_success ? <Tag color="green">ok</Tag> : <Tag color="red">failed</Tag>}
          <span style={{ color: '#888' }}>{r.last_summary}</span></span>
      ) : <span style={{ color: '#888' }}>no run logged yet</span>),
    },
    { title: `Runs (${wf?.days || 30} d)`, dataIndex: 'runs', key: 'runs', width: 90, align: 'right', render: (v) => v || 0 },
    { title: `Cost (${wf?.days || 30} d)`, dataIndex: 'cost_usd', key: 'cost30', width: 90, align: 'right', render: (v) => usd(v) },
  ];

  const costsTab = data && (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}><Statistic title="Today" value={todayCost} precision={2} prefix="$" /></Col>
        <Col span={5}><Statistic title="This month" value={data.totals?.month_cost_usd || 0} precision={2} prefix="$" suffix={`/ ${usd(data.cap_usd)} cap (${capPct}%)`} valueStyle={{ color: capPct >= 80 ? '#cf1322' : undefined }} /></Col>
        <Col span={5}><Statistic title="Sessions this month" value={data.totals?.month_sessions || 0} /></Col>
        <Col span={5}><Statistic title={`All time${data.totals?.since ? ` (since ${String(data.totals.since).slice(0, 10)})` : ''}`} value={data.totals?.cost_usd || 0} precision={2} prefix="$" /></Col>
        <Col span={4}><Statistic title="Sessions all time" value={data.totals?.sessions || 0} /></Col>
      </Row>
      {wf?.by_level && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {wf.by_level.map((b) => (
            <Col span={6} key={b.level}><Statistic title={`Level ${b.level} · ${b.name} (${wf.days} d, ${b.workflows} workflows)`} value={b.cost_usd} precision={2} prefix="$" suffix={`· ${b.runs} runs`} /></Col>
          ))}
        </Row>
      )}
      <h4>Per day, per kind of agent</h4>
      <Table size="small" rowKey="period" dataSource={daily.data} columns={daily.columns} pagination={false} scroll={{ x: true }} style={{ marginBottom: 16 }} data-testid="agentic-daily-table" />
      <h4>Per week, per kind of agent</h4>
      <Table size="small" rowKey="period" dataSource={weekly.data} columns={weekly.columns} pagination={false} scroll={{ x: true }} style={{ marginBottom: 16 }} />
      <h4>Sessions (last {days} days)</h4>
      <Table size="small" rowKey="id" dataSource={data.sessions || []} columns={sessionColumns} pagination={{ pageSize: 25 }} scroll={{ x: true }} />
    </>
  );

  const workflowsTab = (
    <>
      {wf?.error && <Alert type="error" message={wf.error} style={{ marginBottom: 12 }} />}
      {wf?.unregistered_kinds?.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`Sessions logged under kinds that are not in the registry: ${wf.unregistered_kinds.join(', ')} — add them to agentic_workflows.py.`} />
      )}
      <Space style={{ marginBottom: 8 }}>
        <span>Level:</span>
        <Select value={levelFilter} onChange={setLevelFilter} style={{ width: 230 }}
          options={[{ value: 'all', label: 'all levels' }, ...[3, 2, 1, 0].map((lv) => ({ value: String(lv), label: `${lv} · ${LEVEL_NAME[lv]}` }))]} />
        <span style={{ color: '#888' }}>1 = one prompt, one answer · 2 = several steps with tools, a person reads the result · 3 = starts on its own and changes the system</span>
      </Space>
      <Table size="small" rowKey="kind" dataSource={workflowRows} columns={workflowColumns} pagination={false} scroll={{ x: true }} data-testid="agentic-workflows-table" />
    </>
  );

  return (
    <Modal
      title="🤖 Agentic Overview (Dev) — what the AI agents do and cost"
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={1150}
      data-testid="agentic-overview-modal"
    >
      <Space style={{ marginBottom: 12 }}>
        <Select value={days} onChange={setDays} options={[7, 14, 30, 60].map((d) => ({ value: d, label: `last ${d} days` }))} style={{ width: 140 }} />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        {data?.generated_at && <span style={{ color: '#888' }}>as of {data.generated_at} · hosts read: {(data.hosts || []).join(', ')}</span>}
      </Space>
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
      {data?.unreachable?.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`Not included: ${data.unreachable.join('; ')} — the totals below miss that side.`} />
      )}
      {loading && !data ? <Spin /> : data && (
        <Tabs
          defaultActiveKey="costs"
          items={[
            { key: 'costs', label: 'Costs', children: costsTab },
            { key: 'workflows', label: `Workflows (${wf?.workflows?.length || 0})`, children: workflowsTab },
          ]}
        />
      )}
    </Modal>
  );
};

export default AgenticOverviewModal;
